import { Container, Row, Col, Form, Card } from 'react-bootstrap';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../../../utils/apiClient.js';
import { fakeResponse } from '../../../utils/helpers.js';
import { mockIdeasArticle } from '../../../utils/mockData.js';
import AccessModal from '../../../components/Modal/AccessModal.jsx';
import useAccessType from '../../../hooks/useAccessType.js';
import DemoModal from '../../../components/Modal/DemoModal.jsx';
import { useTranslation } from "react-i18next";
import SpinnerButton from '../../../components/Button/SpinnerButton.jsx';
import { RiSparklingFill } from '@remixicon/react';
import Sparkle from '../../../components/Sparkle/Sparkle.jsx';

function ArticleIdeasScreen() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const { getAccessType, isDemo } = useAccessType();
    const [modalType, setModalType] = useState(null);
    const [keyword, setKeyword] = useState(null);
    const [language, setLanguage] = useState(null);
    const languageOptions = t('generatorForm.language.options', { returnObjects: true });
    const [formData, setFormData] = useState({
        language: 'french',
        subject: ''
    });
    const isFormValid = formData.language && formData.subject.trim().length >= 10;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    async function generateIdeas(language, idea) {
        try {
            const res = await authFetch("/api/article/ideas", {
                method: "POST",
                body: JSON.stringify({
                    language,
                    idea
                })
            });

            return await res.json();
        } catch (error) {
            console.error("Error generating ideas:", error);
            return null;
        }
    }

    async function generateFakeIdeas() {
        return fakeResponse(mockIdeasArticle)
    }

    const handleDemo = async () => {
        setIsLoading(true);
        const data = await generateFakeIdeas();
        navigate('/article/ideas/generated', { state: { ideas: data.ideas, keyword, language } });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();
 
        const { language, subject } = formData;

        if (isDemo) {
            setLanguage(language);
            setKeyword(subject);
            setShowModal(true);
            return;
        }
        const accessType = getAccessType();
        if (accessType) {
            setModalType(accessType);
            setShowModal(true);
            return;
        }

        setIsLoading(true);
        const data = await generateIdeas(language, subject);
        navigate('/article/ideas/generated', { state: { ideas: data.ideas, keyword: subject, language } });
    };

    return (
        isLoading ? <Sparkle /> : (
            <Container className="py-5">
                <Row className="mb-5">
                    <Col xs={12} className="text-center">
                        <h2 className="title mb-2">{t('article.generateArticleIdeas')}</h2>
                        <p className="subtitle">{t('generatorForm.subtitle')}</p>
                    </Col>
                </Row>

                <Card>
                    <Card.Body>
                        <Form noValidate onSubmit={handleSubmit} id="ideasForm">
                            <Row className="mb-4 g-4">
                                <Col xs={12}>
                                    <Form.Group controlId="language">
                                        <Form.Label>{`${t('app.language')} *`}</Form.Label>
                                        <Form.Select
                                            className={isLoading ? 'disabled' : ''}
                                            name="language"
                                            required
                                            onChange={handleChange}
                                            value={formData.language}
                                            disabled={isLoading}
                                        >
                                            {Object.entries(languageOptions).map(([key, value]) => (
                                                <option key={key} value={key}>{value}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col xs={12}>
                                    <Form.Group className="mb-3" controlId="subject">
                                        <Form.Label>{`${t('article.keywordArticle')} *`}</Form.Label>
                                        <Form.Control
                                            className={isLoading ? 'disabled' : ''}
                                            type="text"
                                            name="subject"
                                            minLength={10}
                                            maxLength={600}
                                            placeholder={t('article.placeholderSubjectIdea')}
                                            required
                                            disabled={isLoading}
                                            onChange={handleChange}
                                            value={formData.subject}
                                        />
                                        <div className="form-text">{t('generatorForm.subjectMinLength', { min: 10 })}</div>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={12} md={{ span: 6, offset: 6 }}>
                                    <SpinnerButton
                                        type="submit"
                                        className={`bg-pink w-100 ${!isFormValid ? 'disabled' : ''}`}
                                        disabled={isLoading || !isFormValid}
                                        label={<>
                                            <span className="me-2">{t('app.generate')}</span>
                                            <RiSparklingFill size={20} />
                                        </>}
                                    />
                                </Col>
                            </Row>
                        </Form>
                    </Card.Body>
                </Card>

                <AccessModal
                    show={showModal && !isDemo}
                    setShow={setShowModal}
                    modalType={modalType}
                />
                <DemoModal
                    show={showModal && isDemo}
                    setShow={setShowModal}
                    onAction={handleDemo}
                />
            </Container>
        )
    );
}

export default ArticleIdeasScreen;